/**
 * <s-gallery>: premium product gallery (markup: snippets/product-gallery.liquid).
 *
 * - Main slider: one image at a time, slides with a 400ms ease-out; touch swipe follows the finger and
 *   snaps; mouse drag-to-swipe on desktop. Vertical page scroll is left to the browser (touch-action: pan-y).
 * - Thumbnails / dots ([data-gallery-goto]) and hover arrows ([data-gallery-prev]/[data-gallery-next]).
 * - Hover-zoom on fine pointers (2x, follows the cursor).
 * - Tap/click an image ([data-gallery-open]) to open the full-screen <dialog> lightbox, which has its own
 *   swipeable slider; closing it syncs the main slider and returns focus.
 * - Keyboard: ArrowLeft/ArrowRight/Home/End on the focused viewport or inside the lightbox; Escape closes
 *   (native dialog). Off-screen slides are aria-hidden + inert; a polite live region announces the slide.
 */

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DURATION = 400;

class Slider {
  constructor({ viewport, track, slides, rtl, onChange }) {
    this.viewport = viewport;
    this.track = track;
    this.slides = slides;
    this.count = slides.length;
    this.rtl = rtl;
    this.onChange = onChange;
    this.index = 0;
    this.suppressClick = false;
    this.#bind();
    this.go(0, false);
  }

  go(index, animate = true) {
    const next = Math.max(0, Math.min(this.count - 1, index));
    this.index = next;
    this.#setTransform(0, animate);
    this.slides.forEach((slide, i) => {
      const hidden = i !== next;
      slide.toggleAttribute('aria-hidden', hidden);
      if (hidden) slide.setAttribute('aria-hidden', 'true');
      if ('inert' in slide && slide.hasAttribute('data-gallery-slide')) slide.inert = hidden;
    });
    this.onChange?.(next);
  }

  next() { this.go(this.index + 1); }
  prev() { this.go(this.index - 1); }

  #setTransform(dragPx, animate) {
    const sign = this.rtl ? 1 : -1;
    this.track.style.transition = animate ? `transform ${DURATION}ms ${EASE}` : 'none';
    this.track.style.transform = `translate3d(calc(${sign * this.index * 100}% + ${dragPx}px), 0, 0)`;
  }

  #bind() {
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let pointerId = null;
    let dragging = false;
    let cancelled = false;
    let lastDx = 0;

    this.viewport.addEventListener('pointerdown', (event) => {
      if (this.count < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startTime = performance.now();
      dragging = false;
      cancelled = false;
      lastDx = 0;
    });

    this.viewport.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId || cancelled) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!dragging) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          cancelled = true; // vertical scroll wins
          return;
        }
        if (Math.abs(dx) < 8) return;
        dragging = true;
        this.viewport.setPointerCapture?.(pointerId);
        this.viewport.dispatchEvent(new CustomEvent('gallery:dragstart'));
      }
      event.preventDefault();
      const atStart = this.index === 0;
      const atEnd = this.index === this.count - 1;
      const towardStart = this.rtl ? dx < 0 : dx > 0;
      const resist = (towardStart && atStart) || (!towardStart && atEnd);
      lastDx = resist ? dx * 0.35 : dx;
      this.#setTransform(lastDx, false);
    });

    const end = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      const width = this.viewport.clientWidth || 1;
      const elapsed = Math.max(1, performance.now() - startTime);
      const velocity = Math.abs(lastDx) / elapsed;
      const passed = Math.abs(lastDx) > width * 0.18 || (velocity > 0.45 && Math.abs(lastDx) > 20);
      const towardNext = this.rtl ? lastDx > 0 : lastDx < 0;
      if (passed) {
        this.go(this.index + (towardNext ? 1 : -1));
      } else {
        this.go(this.index);
      }
      this.suppressClick = true;
      setTimeout(() => { this.suppressClick = false; }, 0);
    };
    this.viewport.addEventListener('pointerup', end);
    this.viewport.addEventListener('pointercancel', end);

    // A drag must never also count as a click (no lightbox after a swipe).
    this.viewport.addEventListener('click', (event) => {
      if (this.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    // Native image dragging would hijack mouse swipes.
    this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
  }

  handleKey(event) {
    const forward = this.rtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = this.rtl ? 'ArrowRight' : 'ArrowLeft';
    if (event.key === forward) { this.next(); event.preventDefault(); return true; }
    if (event.key === backward) { this.prev(); event.preventDefault(); return true; }
    if (event.key === 'Home') { this.go(0); event.preventDefault(); return true; }
    if (event.key === 'End') { this.go(this.count - 1); event.preventDefault(); return true; }
    return false;
  }
}

class SGallery extends HTMLElement {
  #main = null;
  #lightbox = null;
  #opener = null;
  #ac = null;

  connectedCallback() {
    const viewport = this.querySelector('[data-gallery-viewport]');
    const track = this.querySelector('[data-gallery-track]');
    if (!viewport || !track) return;
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const rtl = getComputedStyle(this).direction === 'rtl';

    const status = this.querySelector('[data-gallery-status]');
    const template = this.dataset.labelTemplate || '';
    const prevBtn = this.querySelector('[data-gallery-prev]');
    const nextBtn = this.querySelector('[data-gallery-next]');
    const gotoBtns = [...this.querySelectorAll('[data-gallery-goto]')];
    const thumbs = this.querySelector('[data-gallery-thumbs]');
    const slides = [...track.querySelectorAll('[data-gallery-slide]')];

    this.#main = new Slider({
      viewport,
      track,
      slides,
      rtl,
      onChange: (i) => {
        if (prevBtn) prevBtn.disabled = i === 0;
        if (nextBtn) nextBtn.disabled = i === slides.length - 1;
        gotoBtns.forEach((btn) => {
          const active = Number(btn.dataset.galleryGoto) === i;
          if (active) btn.setAttribute('aria-current', 'true');
          else btn.removeAttribute('aria-current');
          if (active && thumbs && thumbs.contains(btn)) {
            const left = btn.offsetLeft - (thumbs.clientWidth - btn.clientWidth) / 2;
            thumbs.scrollTo({ left, behavior: 'smooth' });
          }
        });
        if (status && template) {
          status.textContent = template.replace('[n]', String(i + 1));
        }
        this.#resetZoom();
      },
    });

    prevBtn?.addEventListener('click', () => this.#main.prev(), { signal });
    nextBtn?.addEventListener('click', () => this.#main.next(), { signal });
    gotoBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.#main.go(Number(btn.dataset.galleryGoto)), { signal });
    });
    viewport.addEventListener('keydown', (event) => {
      if (event.target === viewport || viewport.contains(event.target)) this.#main.handleKey(event);
    }, { signal });

    this.#setupZoom(viewport, signal);
    this.#setupLightbox(rtl, signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  // ---- Hover zoom (fine pointers only) ----
  #zoomImg = null;

  #resetZoom() {
    if (this.#zoomImg) {
      this.#zoomImg.style.transform = '';
      this.#zoomImg = null;
    }
  }

  #setupZoom(viewport, signal) {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    viewport.addEventListener('mousemove', (event) => {
      if (!fine.matches || event.buttons) return;
      const slide = this.#main.slides[this.#main.index];
      const img = slide?.querySelector('button img');
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      if (this.#zoomImg !== img) {
        this.#resetZoom();
        this.#zoomImg = img;
        img.style.transition = 'transform 200ms ease-out';
      }
      img.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
      img.style.transform = 'scale(2)';
    }, { signal });
    viewport.addEventListener('mouseleave', () => this.#resetZoom(), { signal });
    viewport.addEventListener('gallery:dragstart', () => this.#resetZoom(), { signal });
  }

  // ---- Lightbox ----
  #setupLightbox(rtl, signal) {
    const dialog = this.querySelector('[data-gallery-dialog]');
    const lbViewport = this.querySelector('[data-gallery-lightbox-viewport]');
    const lbTrack = this.querySelector('[data-gallery-lightbox-track]');
    if (!dialog || !lbViewport || !lbTrack) return;
    const lbSlides = [...lbTrack.querySelectorAll('[data-gallery-lightbox-slide]')];
    const count = this.querySelector('[data-gallery-lightbox-count]');
    const prev = this.querySelector('[data-gallery-lightbox-prev]');
    const next = this.querySelector('[data-gallery-lightbox-next]');
    const close = this.querySelector('[data-gallery-close]');

    this.#lightbox = new Slider({
      viewport: lbViewport,
      track: lbTrack,
      slides: lbSlides,
      rtl,
      onChange: (i) => {
        if (count) count.textContent = `${i + 1} / ${lbSlides.length}`;
        if (prev) prev.disabled = i === 0;
        if (next) next.disabled = i === lbSlides.length - 1;
      },
    });

    this.querySelectorAll('[data-gallery-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.#opener = btn;
        this.#resetZoom();
        this.#lightbox.go(Number(btn.dataset.galleryOpen), false);
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
        close?.focus();
      }, { signal });
    });

    prev?.addEventListener('click', () => this.#lightbox.prev(), { signal });
    next?.addEventListener('click', () => this.#lightbox.next(), { signal });
    close?.addEventListener('click', () => dialog.close(), { signal });
    dialog.addEventListener('keydown', (event) => this.#lightbox.handleKey(event), { signal });
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.#main.go(this.#lightbox.index, false);
      const opener = this.#main.slides[this.#main.index]?.querySelector('[data-gallery-open]') || this.#opener;
      opener?.focus({ preventScroll: true });
    }, { signal });
  }
}

if (!customElements.get('s-gallery')) {
  customElements.define('s-gallery', SGallery);
}
